import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Background_Size_Advanced = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">📏 Advanced CSS: background-size</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>background-size</code> property controls how background images
        are scaled inside an element. Beyond the basics (<code>auto</code>,{" "}
        <code>cover</code>, <code>contain</code>), it can be used for{" "}
        <b>
          responsive layouts, parallax effects, retina optimization, patterns
        </b>
        , and even multiple layered designs.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Advanced Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            You can set <b>different sizes for multiple backgrounds</b> using
            comma-separated values.
          </li>
          <li>
            Combine <code>background-size</code> with{" "}
            <code>background-position</code> for precision control.
          </li>
          <li>
            Use percentages for <b>responsive scaling</b> across devices.
          </li>
          <li>
            Works great with <code>background-clip</code> and{" "}
            <code>background-origin</code> for layered effects.
          </li>
          <li>
            Creative trick → Use <code>background-size</code> with gradients to
            make <b>repeating patterns</b>.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax Recap</h2>
        <CodeCopyBlock
          code={`/* Basic */
background-size: auto | cover | contain;

/* Custom size */
background-size: width height;

/* Multiple backgrounds */
background-size: size1, size2, size3;`}
        />
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Advanced Examples</h2>

        {/* Example 1 */}
        <h3 className="section-subtitle">
          1. Multiple Backgrounds with Different Sizes
        </h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("icon.png"), url("banner.jpg");
  background-size: 50px 50px, cover;
  background-position: top left, center;
  background-repeat: no-repeat, no-repeat;
}`}
        />
        <div
          style={{
            width: "320px",
            height: "200px",
            border: "1px solid #ccc",
            borderRadius: "10px",
            backgroundImage:
              "url('https://picsum.photos/60/60'), url('https://picsum.photos/400/300')",
            backgroundSize: "50px 50px, cover",
            backgroundPosition: "top left, center",
            backgroundRepeat: "no-repeat, no-repeat",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 2 */}
        <h3 className="section-subtitle">
          2. Responsive Background with Percentages
        </h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("texture.png");
  background-size: 40% 60%;
}`}
        />
        <div
          style={{
            width: "320px",
            height: "150px",
            border: "1px solid #ccc",
            borderRadius: "10px",
            backgroundImage: "url('https://picsum.photos/200/200')",
            backgroundSize: "40% 60%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 3 */}
        <h3 className="section-subtitle">
          3. Creating Patterns with background-size
        </h3>
        <CodeCopyBlock
          code={`div {
  background-image: linear-gradient(45deg, #facc15 25%, transparent 25%);
  background-size: 40px 40px;
}`}
        />
        <div
          style={{
            width: "320px",
            height: "150px",
            border: "1px solid #ccc",
            borderRadius: "10px",
            backgroundImage:
              "linear-gradient(45deg, #facc15 25%, transparent 25%)",
            backgroundSize: "40px 40px",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 4 */}
        <h3 className="section-subtitle">4. Parallax Effect (with cover)</h3>
        <CodeCopyBlock
          code={`.hero {
  background: url("mountains.jpg") no-repeat center;
  background-size: cover;
  background-attachment: fixed;
}`}
        />
        <div
          style={{
            width: "100%",
            height: "200px",
            borderRadius: "10px",
            backgroundImage:
              "url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1400&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            color: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "1.2rem",
            textShadow: "1px 1px 4px rgba(0,0,0,0.6)",
            marginTop: "0.5rem",
          }}
        >
          🌄 Parallax Effect
        </div>

        {/* Example 5 */}
        <h3 className="section-subtitle">5. Retina Optimization</h3>
        <CodeCopyBlock
          code={`.logo {
  background-image: url("logo@2x.png");
  background-size: 100px 50px; /* shrink retina image */
  width: 100px;
  height: 50px;
}`}
        />
        <div
          style={{
            width: "150px",
            height: "60px",
            border: "1px solid #ccc",
            borderRadius: "10px",
            backgroundImage: "url('https://picsum.photos/300/120')",
            backgroundSize: "150px 60px",
            backgroundRepeat: "no-repeat",
            marginTop: "0.5rem",
          }}
        />
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you know advanced tricks with <code>background-size</code>,
          you can explore{" "}
          <a href="/css/background-attachment" className="learn-more">
            CSS Background Attachment
          </a>{" "}
          for parallax and scroll-based effects.
        </p>
      </div>
    </div>
  );
};

export default Background_Size_Advanced;
